import { getEnv } from 'runtime_env';

/**
 * Per-environment frontend configuration, keyed off `getEnv()`, mirroring `firebase_config.ts`.
 *
 * Everything here ships in the browser bundle, so ONLY publishable values belong in this file.
 * The one frontend secret, the NotificationAPI client secret, arrives on the runtime config
 * channel instead (`window.__ENV__`, written by docker-entrypoint.sh).
 */
export interface EnvConfig {
	stripe_public_key: string;
	recaptcha_site_key: string;
	/** Base64 SPKI. */
	rsa_public_key: string;
	notificationapi_client_id: string;
	/** Optional: Langfuse scoring is deliberately off in staging and hosted_dev. */
	langfuse_public_key?: string;
	langfuse_host?: string;
}

export const NOTIFICATIONAPI_BASE_URL = 'https://api.notificationapi.com';
export const NOTIFICATIONAPI_ACCOUNT_ID = '6v15jj3k6tl68gms6dkndo8s3m';

// Shared by hosted_dev and local development, which differ only in Langfuse below.
const DEV_KEYS = {
	stripe_public_key: 'pk_test_51P4UhQRqHFGDVX8SenoAFZ92YF7ClTqTIkS7Ubh6xV4dzeekK9qkjAyAnku47aTiVcpAFhPRodtqmfKR1s7Hz7kR00tBVUbMoD',
	recaptcha_site_key: '6LevQeYpAAAAAB3KJuNXfPsB5ZjyHkCiqs9dJYcy',
	rsa_public_key: 'MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAw4F7R2G//6/ZmnVpSDnCO8JPQl61yKc7sM4eG+saDM3SICzDrlqcfX2wkbVKofugV2dvZVbvY1Ztrpt/5V1f5+sSXDW0tXjoMEX8mM23pLqoHqRJ2AvieaBULzuzOeyJhckZAmVdNQDla64WgFGMnM51t2GiZXF5s7+jvuoXTjGIhR/IGQzAX+SqnYorYdJmTQQw61CyHOWsRRyFqJSHD5r83pFFgxlgSYxR+Av3bvAQJRZF/kNA1w+ndmoAnsFHXHISAOZ/AjatbbRAuaD/7bu/FNmVNFXi9nZerTBuo4gxb2o/XEFXwCrSX/i8SS7BQxJHlacjWCd+HTaV6sTTJQIDAQAB',
	notificationapi_client_id: '3vqr8550s77tlboqc0cqug31bs'
};

const CONFIGS: Record<string, EnvConfig> = {
	production: {
		stripe_public_key: 'pk_live_51PSiXXJMSwsXg5xplmiJxYTwUqS2btclqWZgPMhvrOpoj88flDzkztwsha1n24Oj0QWMhHEomRl3H0FSpTfBEohR00jXbcFam4',
		recaptcha_site_key: '6LdgDe0pAAAAAIvnOpWnDwFi6CyA5aJggyNArkRr',
		rsa_public_key: 'MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAtJTK1kqDQrSDeEpx09HoyIE+RiH0Bk10V91BNa/ZG/syLsvuYIEcbGUyMbXG1z1TklSbd8hor9/FOEz2rdBW6wU0PsU+BP5TrRKD233Jlzs3rLNh4+twfKU9+fj1jzZxGDq1ddo/GqpuT6tc3GtPhAcizJjLujAJ0UkI2HmMGHuKADh0xGVcI/kf32nSXbGnV72bwtqaGs/l3TA4wAMUU9du4apufuNBEeUZimv9KWX9xjAJRK7huEq7oSFXZtSFp92QY1V+tPBgWSgRdehHnKpFCzg3xl+KAQbFiMjNcfBCFZOKVoMBqiY+jPNabhn5+NNiWNqad2JQbT+Zbok0OwIDAQAB',
		notificationapi_client_id: '6v15jj3k6tl68gms6dkndo8s3m',
		langfuse_public_key: 'pk-lf-350643b7-095c-4bd3-9984-fa769fcf5dcc',
		langfuse_host: 'https://langfuse.i3d.health/'
	},
	staging: {
		stripe_public_key: 'pk_test_51P4UhQRqHFGDVX8SenoAFZ92YF7ClTqTIkS7Ubh6xV4dzeekK9qkjAyAnku47aTiVcpAFhPRodtqmfKR1s7Hz7kR00tBVUbMoD',
		recaptcha_site_key: '6Lf1CNspAAAAANofmzl-JqpaVw-GTRukGXH6d3l3',
		rsa_public_key: 'MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAvzaNoFlKrTZflXuEniC/PzgR+8YPp69dkfjnMpN51ld8bu1TBI9hOzXcLJQ9Uf7x7y/cpK06AfCoz4EPWm4Qe1BEf3d4N/5wBg/Dd1MHWOcXou0IlC1mL9J+5rkPq823KTZJK3k8UfmgDFF+voyHVL1WNeJhCDkOwbfOFB2S2b/DZXNH65D/iU6C3OlNPLf/MehBjkAtC6zbGjBa9Oyvg4lSaSpx2/Hq6bPkAl372vaL01dTW0SK8ffxAJ+gb0y5k8wPhAHN+glzAfp+63HTFkfppk1+dMl/OWDiUl0GUMKRxqCzUf9uy/Hw9gKRef+mRXBCa9yfDmn0P47UU45+dwIDAQAB',
		notificationapi_client_id: 'nznr16uy92bw1ydd0mg7sqkuzf'
	},
	hosted_dev: {
		...DEV_KEYS
	},
	development: {
		...DEV_KEYS,
		langfuse_public_key: 'pk-lf-350643b7-095c-4bd3-9984-fa769fcf5dcc',
		langfuse_host: 'https://langfuse.i3d.health/'
	}
};

/** Unrecognised environments fall back to `development`, as `firebase_config()` does. */
export const env_config = (): EnvConfig => CONFIGS[getEnv() ?? ''] ?? CONFIGS.development;

export default env_config;
